import { Story } from '../../schema';
import {
  useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation,
  usePostStoriesByTripByTripIdMutation,
} from '../../store/backendApi';

interface UseStoryOperationsReturn {
  createStory: () => Promise<Story | null>;
  deleteStory: (storyId: string, confirmFn?: () => boolean) => Promise<void>;
  isCreatingStory: boolean;
  isDeletingStory: boolean;
}

/**
 * Hook to handle CRUD operations for stories.
 * Platform-agnostic: API calls work everywhere, confirmation is parameterized.
 *
 * @param tripId - The trip ID for story operations
 * @returns Story operation functions and loading states
 */
export const useStoryOperations = (tripId?: string): UseStoryOperationsReturn => {
  const [createStoryMutation, { isLoading: isCreatingStory }] =
    usePostStoriesByTripByTripIdMutation();

  const [deleteStoryMutation, { isLoading: isDeletingStory }] =
    useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation();

  const createStory = async (): Promise<Story | null> => {
    if (!tripId) return null;

    try {
      const newStory = await createStoryMutation({
        tripId,
        storyBodyRequest: {
          title: '',
          description: '',
          place: '',
          medias: [],
          date: new Date().toISOString(),
        },
      }).unwrap();
      return newStory;
    } catch (error) {
      return null;
    }
  };

  const deleteStory = async (
    storyId: string,
    confirmFn: () => boolean = () => window.confirm('Are you sure you want to delete this story?'),
  ): Promise<void> => {
    if (!tripId) return;

    const confirmed = confirmFn();
    if (!confirmed) return;

    try {
      await deleteStoryMutation({ tripId, storyId });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    createStory,
    deleteStory,
    isCreatingStory,
    isDeletingStory,
  };
};
